// Mini Crossword: a small grid from words she knows. Tap a cell, read the clue, type.

import { h, clear } from '@kit/dom';
import { makeSurface, roundRect } from '@kit/canvas';
import { attachPointer } from '@kit/pointer';
import type { GameModule, GameContext } from '@shell/types';
import { makeKeyboard } from '@games/common/keyboard';
import { makePuzzle, type Puzzle, type Placed, type Dir } from '@problems/crossword';

function mount(host: HTMLElement, ctx: GameContext) {
  const level = ctx.level;
  const puzzle: Puzzle = makePuzzle(level, ctx.rng);
  const N = puzzle.size;
  const entry: (string | null)[][] = Array.from({ length: N }, () => Array<string | null>(N).fill(null));
  const wrongCells = new Set<string>();
  const reported = new Set<string>();
  const solved = new Set<string>();
  let sel: { r: number; c: number } = { r: puzzle.words[0].row, c: puzzle.words[0].col };
  let dir: Dir = puzzle.words[0].dir;
  let attempts = 0, correct = 0, hints = 0;
  let ended = false;
  const t0 = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, h('strong', null, '0'), ` of ${puzzle.words.length} words`), h('div.row', { style: { gap: '6px' } }, h('button.btn.small.ghost', { onclick: hint }, 'Hint'), h('button.btn.small.primary', { onclick: check }, 'Check')));
  const clue = h('p.center', { style: { fontWeight: '700', minHeight: '44px', padding: '2px 4px' } });
  const stage = h('div', { style: { maxWidth: '360px', margin: '0 auto', width: '100%' } });
  const kb = makeKeyboard({ onKey: type, onBackspace: back });
  host.append(head, clue, stage, kb.el);
  const S = makeSurface(stage, { aspect: 1, onResize: draw });

  const wordAt = (r: number, c: number, d: Dir): Placed | undefined => puzzle.words.find(w => w.dir === d && (d === 'across' ? w.row === r && c >= w.col && c < w.col + w.word.length : w.col === c && r >= w.row && r < w.row + w.word.length));
  const current = (): Placed | undefined => wordAt(sel.r, sel.c, dir) ?? wordAt(sel.r, sel.c, dir === 'across' ? 'down' : 'across');

  function showClue() {
    const w = current();
    if (!w) { clue.textContent = ''; return; }
    if (w.dir !== dir) dir = w.dir;
    clue.textContent = `${w.num} ${w.dir}: ${w.clue} (${w.word.length})`;
  }

  const unPointer = attachPointer(S.canvas, {
    tap(p) {
      const cell = S.w / N;
      const c = Math.floor(p.x / cell), r = Math.floor(p.y / cell);
      if (r < 0 || c < 0 || r >= N || c >= N || puzzle.grid[r][c] === null) return;
      if (sel.r === r && sel.c === c) {
        const other: Dir = dir === 'across' ? 'down' : 'across';
        if (wordAt(r, c, other)) dir = other;
      } else {
        sel = { r, c };
        if (!wordAt(r, c, dir)) dir = dir === 'across' ? 'down' : 'across';
      }
      ctx.sound('tick');
      showClue(); draw();
    },
  });

  function type(k: string) {
    if (ended) return;
    const w = current(); if (!w) return;
    entry[sel.r][sel.c] = k;
    wrongCells.delete(`${sel.r},${sel.c}`);
    // advance within the word
    const dr = dir === 'down' ? 1 : 0, dc = dir === 'across' ? 1 : 0;
    const nr = sel.r + dr, nc = sel.c + dc;
    if (wordAt(nr, nc, dir) === w) sel = { r: nr, c: nc };
    draw();
    autoCheckWord(w);
  }

  function back() {
    if (ended) return;
    if (entry[sel.r][sel.c]) { entry[sel.r][sel.c] = null; }
    else {
      const w = current(); if (!w) return;
      const dr = dir === 'down' ? 1 : 0, dc = dir === 'across' ? 1 : 0;
      const pr = sel.r - dr, pc = sel.c - dc;
      if (wordAt(pr, pc, dir) === w) { sel = { r: pr, c: pc }; entry[sel.r][sel.c] = null; }
    }
    draw();
  }

  function lettersOf(w: Placed): (string | null)[] {
    const dr = w.dir === 'down' ? 1 : 0, dc = w.dir === 'across' ? 1 : 0;
    return Array.from({ length: w.word.length }, (_, i) => entry[w.row + dr * i][w.col + dc * i]);
  }

  /** when a word is first filled completely, report it once (right or wrong) */
  function autoCheckWord(w: Placed) {
    const key = `${w.row},${w.col},${w.dir}`;
    const letters = lettersOf(w);
    if (letters.some(l => l === null) || reported.has(key)) return;
    const ok = letters.join('').toLowerCase() === w.word;
    reported.add(key);
    attempts++;
    if (ok) { correct++; solved.add(key); ctx.sound('ding'); ctx.say(`${w.word.toUpperCase()}! ${solved.size} of ${puzzle.words.length}.`); }
    ctx.report({ skill: 'vocabulary', level, correct: ok, hintUsed: hints > 0, ms: performance.now() - t0, problemId: `cross:${w.word}` });
    (head.querySelector('strong') as HTMLElement).textContent = String(solved.size);
    if (solved.size >= puzzle.words.length) finish();
  }

  function check() {
    if (ended) return;
    wrongCells.clear();
    let wrong = 0, empty = 0;
    for (const w of puzzle.words) {
      const dr = w.dir === 'down' ? 1 : 0, dc = w.dir === 'across' ? 1 : 0;
      for (let i = 0; i < w.word.length; i++) {
        const r = w.row + dr * i, c = w.col + dc * i;
        const e = entry[r][c];
        if (e === null) empty++;
        else if (e.toLowerCase() !== w.word[i]) { wrongCells.add(`${r},${c}`); wrong++; }
      }
      // a word fixed after a wrong report can still be solved
      const key = `${w.row},${w.col},${w.dir}`;
      if (!solved.has(key) && lettersOf(w).join('').toLowerCase() === w.word) { solved.add(key); if (!reported.has(key)) { reported.add(key); attempts++; correct++; ctx.report({ skill: 'vocabulary', level, correct: true, hintUsed: hints > 0, ms: performance.now() - t0, problemId: `cross:${w.word}` }); } }
    }
    (head.querySelector('strong') as HTMLElement).textContent = String(solved.size);
    if (wrong) { ctx.sound('buzz'); ctx.say(`${wrong} letter${wrong > 1 ? 's are' : ' is'} marked in red. Fix them and check again.`); }
    else if (empty) ctx.say(`No wrong letters so far. ${empty} square${empty > 1 ? 's' : ''} still empty.`);
    else { ctx.say('All filled and all right!'); }
    draw();
    if (solved.size >= puzzle.words.length) finish();
  }

  function hint() {
    if (ended) return;
    const w = current(); if (!w) return;
    const dr = w.dir === 'down' ? 1 : 0, dc = w.dir === 'across' ? 1 : 0;
    for (let i = 0; i < w.word.length; i++) {
      const r = w.row + dr * i, c = w.col + dc * i;
      if ((entry[r][c] ?? '').toLowerCase() !== w.word[i]) { entry[r][c] = w.word[i]; wrongCells.delete(`${r},${c}`); hints++; ctx.sound('pop'); ctx.say(`One letter placed. Hints used: ${hints}.`); draw(); autoCheckWord(w); return; }
    }
    ctx.say('That word is already right.');
  }

  function finish() {
    if (ended) return;
    ended = true;
    kb.setEnabled(false);
    ctx.sound('chime');
    if (N === 7 && attempts === correct && hints === 0) ctx.charm('charm.cross');
    setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct && hints === 0, durationMs: performance.now() - t0, notes: [`${puzzle.words.length} words on a ${N} by ${N} grid`, hints ? `Hints used: ${hints}` : 'No hints'] }), 900);
  }

  function draw() {
    const c = S.ctx, W = S.w;
    const cell = W / N;
    c.fillStyle = '#2a2a2e'; c.fillRect(0, 0, W, W);
    const cur = current();
    for (let r = 0; r < N; r++) for (let cc = 0; cc < N; cc++) {
      const x = cc * cell, y = r * cell;
      if (puzzle.grid[r][cc] === null) continue;
      const inWord = cur && wordAt(r, cc, cur.dir) === cur;
      const isSel = sel.r === r && sel.c === cc;
      roundRect(c, x + 2, y + 2, cell - 4, cell - 4, 4);
      c.fillStyle = isSel ? '#f5c542' : inWord ? '#fdf0c2' : '#fff'; c.fill();
      const e = entry[r][cc];
      if (e) {
        c.fillStyle = wrongCells.has(`${r},${cc}`) ? '#e2574c' : '#2a2a2e';
        c.font = `800 ${Math.round(cell * 0.5)}px Nunito, sans-serif`; c.textAlign = 'center'; c.textBaseline = 'middle';
        c.fillText(e.toUpperCase(), x + cell / 2, y + cell / 2 + 2);
      }
      const start = puzzle.words.find(w => w.row === r && w.col === cc);
      if (start) { c.fillStyle = '#6a6a72'; c.font = `700 ${Math.round(cell * 0.2)}px Nunito, sans-serif`; c.textAlign = 'left'; c.textBaseline = 'top'; c.fillText(String(start.num), x + 5, y + 4); }
    }
  }

  showClue(); draw();
  ctx.say('Tap a square, read the clue, type the word.');
  return { destroy() { unPointer(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'words.mini-crossword',
  title: 'Mini Crossword',
  blurb: 'A little crossword from words you know. Tap a square and type.',
  category: 'words',
  skills: ['vocabulary', 'phonics_spelling'],
  levelRange: [1, 10],
  unlock: { stars: 16 },
  mount,
};
export default mod;
